import React, { useCallback, useContext, useState } from 'react';
import { media, style } from 'typestyle';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ChevronDoubleLeftIcon from 'mdi-react/ChevronDoubleLeftIcon';
import ChevronDoubleRightIcon from 'mdi-react/ChevronDoubleRightIcon';
import * as styles from './navigation.styles';
import NavigationItem, { type NavigationIcon } from '../navigation-item/navigation-item.component';
import Column from '../column/column.component';
import NavigationParent from '../navigation-parent/navigation-parent.component';
import { MainAxisAlignment } from '../flex/flex.component';
import enFlag from '../../../assets/images/englishFlag.png';
import nlFlag from '../../../assets/images/dutchFlag.png';
import Row from '../row/row.component';
import OFFICE_INFORMATION_KEY from '../../../features/offices/consts/consts';
import i18n, { LANGUAGE_STORAGE_KEY } from '../../../i18n';
import AppConstants from '../../app.constants';
import RouteConstants from '../../routes.constants';
import { useKeycloak } from '../../../features/authentication/keycloak/KeycloakProvider';
import UserProfile from '../user-profile/user-profile.component';
import { IsMobileContext } from '../../context/is-mobile';
import mergeClasses from '../../utils/merge-classes';
import packageJson from '../../../../package.json';

const { version } = packageJson;

interface MenuItem {
  readonly title: string;
  readonly activePath: string;
  readonly icon?: NavigationIcon;
}

export interface LinkMenuItem extends MenuItem {
  readonly path: string;
}

export interface ParentMenuItem extends MenuItem {
  readonly children: LinkMenuItem[];
}

interface Props {
  readonly open: boolean;
  readonly items: Array<LinkMenuItem | ParentMenuItem>;
  readonly logoutItem?: LinkMenuItem;
  closeMenu: () => void;
}

export const Navigation = ({ open, items, logoutItem, closeMenu }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const keycloak = useKeycloak();
  const isMobile = useContext(IsMobileContext);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(
    () => window.localStorage.getItem(AppConstants.NAVIGATION_COLLAPSED_STORAGE_KEY) === 'true',
  );

  // Collapsing only applies to the desktop rail; the mobile drawer is always full width.
  const collapsed = !isMobile && isCollapsed;

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((previous) => {
      const next = !previous;
      window.localStorage.setItem(AppConstants.NAVIGATION_COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  }, []);

  const changeLanguage = (lng: string | undefined): void => {
    if (lng !== undefined) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
    }
    i18n.changeLanguage(lng);
  };

  const officeStorageKeys = [OFFICE_INFORMATION_KEY];

  const renderItem = (item: LinkMenuItem): React.JSX.Element => (
    <NavigationItem
      key={item.path + item.activePath}
      title={item.title}
      icon={item.icon}
      isCollapsed={collapsed}
      isActive={location.pathname.match(item.activePath) != null}
      onClick={(): void => {
        closeMenu();
        if (item.path === RouteConstants.PATH_ROOT) {
          keycloak.logout({});
        } else {
          if (location.pathname.includes('/kantoren/')) {
            officeStorageKeys.forEach((key) => {
              window.localStorage.removeItem(key);
            });
          }
          navigate(`/${item.path}`);
        }
      }}
    />
  );

  const currentLanguage = i18n.language?.split('-')[0];
  const collapseLabel = (collapsed ? t('expand-menu') : t('collapse-menu')) ?? '';

  return (
    <Column
      className={style(
        styles.navigation(collapsed),
        media({ maxWidth: AppConstants.MINIMUM_DESKTOP_WIDTH }, styles.navigationMobile(open, collapsed)),
      )}
      mainAxisAlignment={MainAxisAlignment.Start}
    >
      <div className={style(styles.glow)} />
      <UserProfile isCollapsed={collapsed} />

      <Column className={style(styles.menuSection)} itemSpacing={4}>
        <span className={style(styles.menuHeader(collapsed))}>{t('menu').toUpperCase()}</span>
        {items.map((item) => {
          if ('children' in item) {
            return (
              <NavigationParent
                key={item.title}
                title={item.title}
                icon={item.icon}
                isCollapsed={collapsed}
                isOpen={location.pathname === item.activePath}
                isActive={location.pathname === item.activePath}
              >
                {item.children.map((child) => renderItem(child))}
              </NavigationParent>
            );
          }

          return renderItem(item);
        })}
      </Column>

      {logoutItem != null && (
        <>
          <div className={style(styles.divider)} />
          <div className={style(styles.logoutSection)}>{renderItem(logoutItem)}</div>
        </>
      )}

      <div className={style(styles.brandLockup(collapsed))}>
        <div className={style(styles.whyellowLogo(collapsed))} />
      </div>

      <div className={style(styles.divider)} />

      <div className={style(styles.footer(collapsed))}>
        <Row itemSpacing={6} mainAxisAlignment={MainAxisAlignment.Start}>
          <img
            className={mergeClasses(style(styles.flags), currentLanguage === 'nl' ? style(styles.activeFlag) : undefined)}
            src={nlFlag}
            alt="nl"
            onClick={(): void => {
              changeLanguage('nl');
            }}
            role="presentation"
          />
          <img
            className={mergeClasses(style(styles.flags), currentLanguage === 'en' ? style(styles.activeFlag) : undefined)}
            src={enFlag}
            alt="en"
            onClick={(): void => {
              changeLanguage('en');
            }}
            role="presentation"
          />
        </Row>
        {!isMobile && (
          <button
            type="button"
            className={style(styles.collapseToggle)}
            aria-label={collapseLabel}
            title={collapseLabel}
            onClick={toggleCollapsed}
          >
            {collapsed ? <ChevronDoubleRightIcon size={15} /> : <ChevronDoubleLeftIcon size={15} />}
          </button>
        )}
      </div>

      <span className={style(styles.versionText(collapsed))}>v{version}</span>
    </Column>
  );
};
